import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

/**
 * The collapsed single-row layout, shared by `variant="minimal"` and `isSticky`. Both apply the
 * exact same overrides, so the two variants are idempotent when combined. The expanded layout
 * lives in `base` — `variant="default"` and `isSticky: false` deliberately define nothing, so
 * neither can undo the other's collapse.
 *
 * Every override is scoped to `tablet` and up, with no `base` key: below the breakpoint the `base`
 * styles win and the header stays expanded. The mobile Figma frame has no sticky or minimal
 * variant — it always shows the title as a heading — and a single 72px row cannot hold a
 * breadcrumb plus full-width actions at 375px.
 *
 * The slots the collapsed row drops are hidden here rather than omitted from the DOM, so the
 * component never has to know the viewport: no breakpoint hook, and no wrong-layout first paint.
 */
const collapsedSlots = {
  headerBlock: {
    tablet: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: '24',
      // No 72 in the size scale — the design fixes the collapsed row height.
      minHeight: rem(72),
      paddingBlockEnd: '12',
      paddingBlockStart: '12',
      paddingInlineEnd: '32',
      paddingInlineStart: '24',
    },
  },
  breadcrumb: {
    tablet: {
      flex: '0 1 auto',
      minWidth: 0,
    },
  },
  contentBlock: {
    tablet: {
      alignItems: 'center',
      flex: '1 0 0',
      // 24 between the title and the actions, per `pageType=main, isSticky=true`. The 8 belongs to
      // `actionBlock`, between the buttons.
      gap: '24',
      justifyContent: 'flex-end',
      minWidth: 0,
    },
  },
  titleBlock: {
    tablet: {
      alignItems: 'center',
    },
  },
  title: {
    tablet: {
      textStyle: 'heading/h3',
    },
  },
  actionBlock: {
    tablet: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: '8',
      width: 'auto',
    },
  },
  avatar: {
    tablet: { display: 'none' },
  },
  badge: {
    tablet: { display: 'none' },
  },
  secondaryText: {
    tablet: { display: 'none' },
  },
  tabs: {
    tablet: { display: 'none' },
  },
} as const;

/**
 * Collapsed *and* carrying a breadcrumb: the trail shows the title as its current item, so the
 * heading would duplicate it. `Breadcrumb.CurrentLink` is a link and not a heading, so the block is
 * made screen-reader-only instead of hidden — that keeps exactly one `h1` on the page at every
 * width without a second, conditionally rendered heading node.
 */
const titleInBreadcrumb = {
  titleBlock: { tablet: { srOnly: true } },
  // The row is trail + actions. The actions cannot shrink, so leaving `contentBlock` on a zero
  // basis let a long trail take the whole row and collapse it to width 0 — the buttons then
  // overflowed their own box and painted on top of the trail. Sizing the actions to their content
  // and letting the trail take what is left makes the trail yield, and its last crumb ellipsise.
  breadcrumb: { tablet: { flex: '1 1 auto' } },
  // `width: auto` matters as much as the flex here, for the same reason it does in `controlsRow`:
  // the base width is `100%`, which flex-basis `auto` resolves to, so without it this block claims
  // the whole row and squeezes the trail to nothing.
  contentBlock: { tablet: { flex: '0 0 auto', width: 'auto' } },
} as const;

/**
 * `minimal` *with* inline controls: the row carries three groups — trail, controls, actions — spread
 * apart rather than the usual trail-left/actions-right pair. `contentBlock` gives up its `flex: 1`
 * so `space-between` has room to distribute; without that it would absorb the free space and pin
 * the controls against the actions.
 *
 * Paired with both collapse modes, since `minimal` and `isSticky` produce the same row through
 * `collapsedSlots` — pairing it with `minimal` alone left the sticky row dropping the middle group.
 * This is the row the design file calls `PageHeader-WFE`.
 */
const controlsRow = {
  headerBlock: { tablet: { justifyContent: 'space-between' } },
  // Undoes the `flex: 1 1 auto` that `titleInBreadcrumb` gives the trail. That growth is right for
  // the two-group row (trail + actions), but here a third group sits between them: a growing trail
  // swallows all the free space on its side and shoves the controls island against the actions.
  // Shrink-only keeps the trail ellipsising when the row is tight, while `space-between` gets free
  // space to distribute.
  breadcrumb: { tablet: { flex: '0 1 auto' } },
  // `width: auto` matters as much as the flex: the base width is `100%`, which flex-basis `auto`
  // resolves to, so the block would otherwise claim the whole row and shrink the trail until its
  // metadata truncated away. Sized to its actions instead, leaving the free space to distribute.
  contentBlock: { tablet: { flex: '0 0 auto', width: 'auto' } },
} as const;

/**
 * Collapsed with inline controls but no trail: the title takes the left slot, so the three groups
 * are the title, the controls and the actions — all inside `contentBlock`, which already spans the
 * row. The title block gives up its `flex: 1` so `space-between` has free space to distribute; left
 * growing, it would absorb the row and shove the controls against the actions.
 */
const controlsWithoutTrail = {
  contentBlock: { tablet: { justifyContent: 'space-between' } },
  titleBlock: { tablet: { flex: '0 1 auto' } },
} as const;

/**
 * `alignsToContent`'s 32, restated for the collapsed row. The collapsed slots set the header block's
 * start padding under the same `tablet` key, and the variant applied later wins the merge -- which
 * is the collapsed one, since `isSticky` and `variant` come after `alignsToContent`. A compound
 * variant is applied after every plain one, so this holds whatever order the props arrive in.
 */
const contentAlignedCollapsed = {
  headerBlock: { tablet: { paddingInlineStart: '32' } },
} as const;

/**
 * Shared by the `app` and `user` content shapes — a larger avatar centred against a smaller title
 * and 14px secondary text. The two differ only in the avatar-to-text gap.
 */
const entityContent = {
  titleBlock: {
    alignItems: 'center',
  },
  title: {
    textStyle: { base: 'heading/mobile/h2', tablet: 'heading/h2' },
  },
  secondaryText: {
    textStyle: 'body/md/regular',
  },
} as const;

const pageHeaderSlotRecipe = defineSlotRecipe({
  className: 'page-header',
  slots: [
    'root',
    'headerBlock',
    'breadcrumb',
    'controls',
    'contentBlock',
    'titleBar',
    'titleBlock',
    'avatar',
    'textBlock',
    'titleRow',
    'title',
    'badge',
    'secondaryText',
    'actionBlock',
    'menuButton',
    'sectionBlock',
    'sectionTitle',
    'tabs',
  ] as const,
  base: {
    root: {
      background: 'background/primary',
      borderBlockEnd: '1px solid',
      borderColor: 'border/regular',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    headerBlock: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      gap: { base: '16', tablet: '24' },
      paddingBlockEnd: { base: '16', tablet: '24' },
      paddingBlockStart: { base: '16', tablet: '24' },
      paddingInlineEnd: { base: '16', tablet: '32' },
      paddingInlineStart: { base: '16', tablet: '24' },
      width: '100%',
    },
    breadcrumb: {
      display: 'flex',
      maxWidth: '100%',
      minWidth: 0,
      // The trail's list is `white-space: nowrap`, so without this a long one escapes the header's
      // right edge instead of being clipped by it. `BitkitBreadcrumb` ellipsises its last crumb on
      // its own, but only once it is allowed to shrink — see the `breadcrumb` prop docs.
      overflow: 'hidden',
    },
    controls: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
    },
    contentBlock: {
      alignItems: { base: 'flex-start', tablet: 'flex-end' },
      display: 'flex',
      flexDirection: { base: 'column', tablet: 'row' },
      gap: { base: '16', tablet: '32' },
      width: '100%',
    },
    // The title block and the menu button, which `hasSidebar` wraps them in. It stands in for the
    // title block in the content row from `tablet`, where the button is gone.
    // `contents` from `tablet`: the wrapper drops out of the layout there, so the title block is a
    // direct child of the content row again and every desktop rule — collapsed, controls, trail —
    // keeps targeting it as before.
    titleBar: {
      alignItems: 'center',
      display: { base: 'flex', tablet: 'contents' },
      gap: '12',
      minWidth: 0,
      width: '100%',
    },
    titleBlock: {
      display: 'flex',
      flex: '1 0 0',
      minWidth: 0,
    },
    avatar: {
      display: 'flex',
      flexShrink: 0,
    },
    textBlock: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    titleRow: {
      alignItems: 'center',
      display: 'flex',
      gap: '12',
      minWidth: 0,
      paddingInlineEnd: { base: '0', tablet: '24' },
    },
    title: {
      color: 'text/primary',
      minWidth: 0,
      textStyle: { base: 'heading/mobile/h1', tablet: 'heading/h1' },
      // One line with an ellipsis rather than wrapping. A wrapped title grew the header without
      // limit, and the collapsed row is a fixed 72px that a second line would break. The component
      // pairs this with `BitkitOverflowTooltip` so the full string stays readable.
      truncate: true,
    },
    badge: {
      display: 'flex',
      flexShrink: 0,
      // The design nudges the badge below the optical centre of the title line, wrapping it in
      // `flex-col h-full justify-center pt-8`. Reproduced with the same mechanism rather than a
      // fixed offset: the row centres the margin box, so this lands the badge `8 / 2` lower —
      // 12px down a 40px desktop line and 10px down a 36px mobile one, matching both frames.
      marginBlockStart: '8',
    },
    secondaryText: {
      color: 'text/secondary',
      textStyle: 'body/lg/regular',
      truncate: true,
    },
    actionBlock: {
      // The design puts the primary action on top once the actions stack, so `base` reverses them
      // visually while the DOM keeps its secondary-then-primary order for the row layout.
      //
      // That mismatch is deliberate and settled — please don't "fix" it. Actions arrive as an opaque
      // `children` list, so with one DOM order and two visual orders, no arrangement satisfies both
      // breakpoints; flipping the DOM only moves the mismatch to the row. The alternatives are a
      // design change or a structured actions API, and neither earns its cost: the surviving case is
      // a zoomed desktop window rendering `base`, where focus steps up rather than down between two
      // adjacent buttons and nothing becomes unreachable. Chasing visual order through the DOM is
      // also a pattern the design system avoids. v1 ships the same behaviour in `SubHeader.tsx:96`.
      alignItems: { base: 'stretch', tablet: 'flex-end' },
      display: 'flex',
      flexDirection: { base: 'column-reverse', tablet: 'row' },
      flexShrink: 0,
      gap: '12',
      justifyContent: 'flex-end',
      width: { base: '100%', tablet: 'auto' },
    },
    // `hasSidebar` only, and mobile only: the button that opens the sidebar's drawer, and the
    // active section's name under a rule. From `tablet` the sidebar is beside the page and names
    // the section itself, so both go.
    menuButton: {
      display: { base: 'flex', tablet: 'none' },
      flexShrink: 0,
    },
    sectionBlock: {
      // The content block aligns its children to the start, so the rule has to ask for the width.
      alignSelf: 'stretch',
      borderBlockStartColor: 'border/minimal',
      borderBlockStartStyle: 'solid',
      borderBlockStartWidth: '1',
      display: { base: 'flex', tablet: 'none' },
      flexDirection: 'column',
      // The rule runs edge to edge through the header block's inline padding.
      marginInline: '-16',
      paddingBlockStart: '16',
      paddingInline: '16',
    },
    sectionTitle: {
      color: 'text/primary',
      textStyle: 'heading/mobile/h1',
      truncate: true,
    },
    tabs: {
      // The tab list draws its own bottom border, inset by this padding. Pulling the row up by 1px
      // lands it exactly on the root's border instead of stacking a second line above it, so the
      // header keeps one full-width bottom edge as designed.
      marginBlockEnd: rem(-1),
      paddingInlineEnd: { base: '0', tablet: '32' },
      paddingInlineStart: { base: '0', tablet: '16' },
      width: '100%',
    },
  },
  compoundVariants: [
    // The with-sidebar header shows the page title small beside the menu button, and the section
    // below it large: `heading/mobile/h2` after a trail (`mobilePageHeader_content_withSidebar`),
    // `heading/mobile/h3` without one, where the row is the design's `sidebar-block`.
    {
      contentVariant: 'title',
      hasBreadcrumb: true,
      hasSidebar: true,
      css: { title: { base: { textStyle: 'heading/mobile/h2' } } },
    },
    { hasBreadcrumb: true, variant: 'minimal', css: titleInBreadcrumb },
    { hasBreadcrumb: true, isSticky: true, css: titleInBreadcrumb },
    { hasBreadcrumb: true, hasControls: true, variant: 'minimal', css: controlsRow },
    { hasBreadcrumb: true, hasControls: true, isSticky: true, css: controlsRow },
    { hasBreadcrumb: false, hasControls: true, variant: 'minimal', css: controlsWithoutTrail },
    { hasBreadcrumb: false, hasControls: true, isSticky: true, css: controlsWithoutTrail },
    { alignsToContent: true, variant: 'minimal', css: contentAlignedCollapsed },
    { alignsToContent: true, isSticky: true, css: contentAlignedCollapsed },
  ],
  variants: {
    contentVariant: {
      title: {
        titleBlock: {
          // `center` rather than the design's `flex-end`. With no secondary text the two are
          // identical — a 40px avatar against a 40px title line — so the bare sub-page card is
          // unaffected. With secondary text present, `flex-end` dropped the avatar down beside it;
          // centring matches how the app card sits its avatar against a two-line block.
          alignItems: 'center',
          gap: '12',
        },
        textBlock: {
          gap: '4',
        },
      },
      // `app` and `user` differ only in the gap between the avatar and the text — 16 and 12
      // respectively — so they share everything else via `entityContent`.
      app: {
        ...entityContent,
        titleBlock: {
          ...entityContent.titleBlock,
          gap: '16',
        },
      },
      user: {
        ...entityContent,
        titleBlock: {
          ...entityContent.titleBlock,
          gap: '12',
        },
      },
    },
    // Mobile splits the trail into its own `breadcrumb-margin` block (`pt-24 px-16`) above a
    // `p-16` header block, so the breadcrumb sits 24 from the top rather than 16. Desktop is 24
    // either way, hence the flat value.
    hasBreadcrumb: {
      true: {
        headerBlock: {
          paddingBlockStart: '24',
        },
      },
      false: {},
    },
    hasControls: {
      true: {},
      false: {},
    },
    /**
     * From the component's `onMenuClick`: the mobile layout for a page with a sidebar
     * (`PageHeader - mobile`, `hasSidebar=true`). The title row tightens up — 12 above, 8 to the
     * trail — and the title steps down to leave the section name as the page's large heading. All
     * `base`: from `tablet` the header is the ordinary one.
     */
    hasSidebar: {
      true: {
        headerBlock: {
          base: {
            gap: '8',
            paddingBlockStart: '12',
          },
        },
        title: {
          base: { textStyle: 'heading/mobile/h3' },
        },
      },
      false: {},
    },
    /**
     * From the component's `showDivider`. The header's background is the page's, so without the line
     * the bottom padding reads as nothing but a margin -- one the page cannot shrink -- and it goes
     * with the line. Flat values, so the collapsed row's `tablet` padding still centres it, and
     * `isSticky` restores the line from `tablet`.
     */
    hasDivider: {
      true: {},
      false: {
        root: { borderBlockEndWidth: 0 },
        headerBlock: { paddingBlockEnd: 0 },
      },
    },
    isSticky: {
      true: {
        root: {
          // Scoped to `tablet` like the layout overrides: below the breakpoint `isSticky` is
          // ignored entirely, so the expanded header must not pick up the stuck chrome either.
          tablet: {
            // Content scrolls under a stuck header, so it keeps its line with `hasDivider: false`
            // too. Width only: the `borderBlockEnd` shorthand would also reset the colour.
            borderBlockEndWidth: '1',
            borderColor: 'border/minimal',
            boxShadow: 'elevation/sm',
          },
        },
        ...collapsedSlots,
      },
      false: {},
    },
    variant: {
      default: {},
      minimal: collapsedSlots,
    },
    /**
     * Lines the content block up with the page content below, leaving the root full-width — so the
     * header's background and bottom border run edge to edge while the title sits where the content
     * does. The cap, when there is one, is a `maxWidth` the component sets from `contentWidth`, since
     * a recipe variant cannot carry an arbitrary length; `contentAlign` places it, and does nothing
     * when `contentWidth` is `'full'` and there is no cap.
     *
     * The design's centred frame pads both sides with 32, where the default header starts at 24.
     * Only from `tablet`: below it the base 16 stands, matching what a content column pads with
     * there, while the cap itself stays unscoped so the two track each other at every width.
     *
     * The collapsed row (`isSticky`, `minimal`) resets the start padding to 24 under the same
     * `tablet` key and wins that merge, so the 32 is restated there by a compound variant — see
     * `contentAlignedCollapsed`.
     */
    alignsToContent: {
      true: {
        headerBlock: {
          tablet: {
            paddingInlineStart: '32',
          },
        },
      },
      false: {},
    },
    /**
     * Where the capped block sits in the full-width root: centred over a centred content column, or
     * at the start over one that starts against a sidebar. Without a cap the block spans the root, so
     * the auto margins resolve to nothing and either value renders the same.
     */
    contentAlign: {
      center: {
        headerBlock: {
          marginInline: 'auto',
        },
      },
      start: {
        headerBlock: {
          marginInlineEnd: 'auto',
        },
      },
    },
  },
  defaultVariants: {
    alignsToContent: false,
    contentAlign: 'center',
    contentVariant: 'title',
    hasBreadcrumb: false,
    hasControls: false,
    hasDivider: true,
    hasSidebar: false,
    isSticky: false,
    variant: 'default',
  },
});

export default pageHeaderSlotRecipe;
